/**
 * Helpers partages pour formater le detail d'une simulation
 * `simulatorSpacePublicMutation` (resume cartridge + search card).
 *
 * - `tt({ t, key, params, fallback })` : t() avec fallback quand la cle
 *   n'est pas remplie cote backoffice trad (translate.js renvoie la cle brute).
 * - `buildDetailLines({ data, t })` : transforme `data.priceByPeriod` en lignes
 *   pretes a afficher, avec agregation par fullday/halfday et fusion du
 *   supplement byperson en suffixe `(dont X pers. x Y€ / jour)`.
 *   Format de sortie : Array<{ key: string, text: string, total: number }>.
 * - `buildPriceHeadline({ data, t })` : choisit CE QUE montre le grand chiffre du
 *   cartridge selon la nature des dates selectionnees (cf. plus bas).
 */

export const tt = ({
  t, key, params, fallback,
}) => {
  const result = t(key, params);
  return result === key ? fallback : result;
};

const buildExprLine = ({ items }) => {
  const sorted = [...items].sort((a, b) => (a?.amount ?? 0) - (b?.amount ?? 0));
  const exprParts = sorted.map((it) => {
    const amount = it?.amount ?? 0;
    const counter = it?.counter ?? 0;
    return counter > 1 ? `${amount} X ${counter}` : `${amount}`;
  });
  const expr = exprParts.join(' + ');
  const total = sorted.reduce((sum, it) => sum + (it?.total ?? 0), 0);
  return { expr, total };
};

export const buildDetailLines = ({ data, t }) => {
  const priceByPeriod = data?.priceByPeriod || [];
  const fulldayItems = priceByPeriod.filter((i) => i?.byYieldId === 'fullday');
  const halfdayItems = priceByPeriod.filter((i) => i?.byYieldId === 'halfday');
  // WEA-163 - lignes des dates reservees a l'heure. Sans ce filtre, elles n'auraient
  // correspondu a aucune categorie et le detail du cartridge les aurait passees sous
  // silence, alors qu'elles portent le montant.
  const hourlyItems = priceByPeriod.filter((i) => i?.byYieldId === 'hourly');
  const byPersonItem = priceByPeriod.find((i) => i?.byYieldId === 'byperson');

  const byPersonSuffix = byPersonItem
    ? tt({
      t,
      key: 'space:simulator.byperson_suffix',
      params: {
        counter: byPersonItem?.counter ?? 0,
        amount: byPersonItem?.amount ?? 0,
      },
      fallback: ` (dont ${byPersonItem?.counter ?? 0} pers. × ${byPersonItem?.amount ?? 0}€ / jour)`,
    })
    : '';

  const lines = [];
  if (fulldayItems.length) {
    const { expr, total } = buildExprLine({ items: fulldayItems });
    const baseText = tt({
      t,
      key: 'space:simulator.fullday_line',
      params: { expr },
      fallback: `Journée entière : ${expr}`,
    });
    lines.push({
      key: 'fullday',
      text: `${baseText}${byPersonSuffix}`,
      total,
    });
  }
  if (halfdayItems.length) {
    const { expr, total } = buildExprLine({ items: halfdayItems });
    const baseText = tt({
      t,
      key: 'space:simulator.halfday_line',
      params: { expr },
      fallback: `Demi-journée : ${expr}`,
    });
    lines.push({
      key: 'halfday',
      text: `${baseText}${byPersonSuffix}`,
      total,
    });
  }

  if (hourlyItems.length) {
    const { expr, total } = buildExprLine({ items: hourlyItems });
    const baseText = tt({
      t,
      key: 'space:simulator.hourly_line',
      params: { expr },
      fallback: `À l'heure : ${expr}`,
    });
    lines.push({
      key: 'hourly',
      text: `${baseText}${byPersonSuffix}`,
      total,
    });

    /*
      WEA-163 — rappel du volume horaire. Le détail liste un montant par groupe de
      durée (deux dates de durées différentes font deux lignes) : sans ce total, le
      client doit additionner lui-même pour savoir combien d'heures il réserve.
      Ligne sans montant — c'est un récapitulatif, pas une ligne de prix.
    */
    const totalHours = hourlyItems.reduce((acc, i) => acc + (Number(i?.hours) || 0), 0);
    if (totalHours > 0) {
      lines.push({
        key: 'hourly-total',
        text: tt({
          t,
          key: 'space:simulator.hours_total_line',
          params: { hours: totalHours },
          fallback: `Total des heures sélectionnées : ${totalHours} h`,
        }),
        total: null,
      });
    }
  }

  return lines;
};

export const hasYieldOnDates = ({ data }) => !!data?.hasYieldByDate;

/*
  ────────────────────────────────────────────────────────────────────────────
  En-tete de prix du cartridge : le grand chiffre ne disparait jamais, il
  change d'unite.

  Avant, des que la tarification variait d'une date a l'autre le bloc entier
  etait retire du DOM (`hasYieldOnDates`) : plus de prix, plus de barre
  fidelite, plus d'etoile HOF (Hall Of Fame). On remplace ce trou par trois
  modes, choisis a partir de la SEULE nature des dates selectionnees :

    - `unit`  : une seule nature, un seul montant  -> « 3600€ par jour »
    - `range` : une seule nature, montants differents (yield par date)
                -> « 3600 – 4200€ par jour »
    - `total` : natures melangees, aucune unite commune n'existe
                -> total de la selection + recapitulatif du melange

  Volontairement PAS de « moyenne par jour » sur un melange jour/heure : une
  heure n'est pas une fraction de journee dans le moteur, le client ne
  retrouverait ce chiffre nulle part sur sa facture.
  ────────────────────────────────────────────────────────────────────────────
*/

// Ordre d'affichage du recapitulatif. La « nuit » (period 3) n'est plus
// commercialisee : elle n'a donc pas de nature ici.
const HEADLINE_NATURES = ['fullday', 'halfday', 'hourly'];

const round2 = (value) => Math.round(Number(value) * 100) / 100;

/**
 * Montant unitaire d'un groupe, dans l'unite de sa nature.
 *
 * Pour l'horaire, `amount` est le prix d'UNE DATE (tarif horaire x duree) et
 * non un tarif horaire : on redivise par `hoursEach`, la duree unitaire du
 * groupe. Quand le groupe melange des durees le moteur ne remonte pas
 * `hoursEach` (il n'y a pas de duree unitaire vraie) : on renvoie null, ce qui
 * fait basculer l'en-tete en mode `total` plutot que d'inventer un tarif.
 */
const unitAmountOf = (item) => {
  if (item?.byYieldId !== 'hourly') return round2(item?.amount);
  const each = Number(item?.hoursEach) || 0;
  if (each <= 0) return null;
  return round2(Number(item?.amount || 0) / each);
};

const sumCounters = ({ items, nature }) => items
  .filter((i) => i?.byYieldId === nature)
  .reduce((sum, i) => sum + (Number(i?.counter) || 0), 0);

const sumHours = ({ items }) => items
  .filter((i) => i?.byYieldId === 'hourly')
  .reduce((sum, i) => sum + (Number(i?.hours) || 0), 0);

/** Pastilles « 2 jours · 1 demi-journee · 6 h », dans l'ordre HEADLINE_NATURES. */
const buildParts = ({ items, t }) => {
  const parts = [];

  const days = sumCounters({ items, nature: 'fullday' });
  if (days > 0) {
    parts.push({
      key: 'fullday',
      text: tt({
        t,
        key: days > 1 ? 'space:simulator.part_days' : 'space:simulator.part_day',
        params: { count: days },
        fallback: `${days} jour${days > 1 ? 's' : ''}`,
      }),
    });
  }

  const halves = sumCounters({ items, nature: 'halfday' });
  if (halves > 0) {
    parts.push({
      key: 'halfday',
      text: tt({
        t,
        key: halves > 1 ? 'space:simulator.part_halfdays' : 'space:simulator.part_halfday',
        params: { count: halves },
        fallback: `${halves} demi-journée${halves > 1 ? 's' : ''}`,
      }),
    });
  }

  const hours = round2(sumHours({ items }));
  if (hours > 0) {
    parts.push({
      key: 'hourly',
      text: tt({
        t,
        key: 'space:simulator.part_hours',
        params: { count: hours },
        fallback: `${hours} h`,
      }),
    });
  }

  return parts;
};

const UNIT_LABEL = {
  fullday: { key: 'space:formules.day', fallback: 'jour' },
  halfday: { key: 'extra:formule.half', fallback: 'demi-journée' },
  hourly: { key: 'space:formules.hour', fallback: 'heure' },
};

/**
 * @param {Object} params.data - reponse `simulatorSpacePublicMutation`
 * @param {Function} params.t
 * @returns {Object|null} null tant qu'aucune date exploitable n'est selectionnee
 *   (l'appelant retombe alors sur le prix de reference du lieu), sinon
 *   { mode, unit, unitLabel, amount, amountMax, strike, incl_tax, parts, count }
 */
export const buildPriceHeadline = ({ data, t }) => {
  const items = (data?.priceByPeriod || [])
    // `byperson` est un supplement, pas une nature de date : l'inclure ferait
    // croire a un melange des qu'il y a des riders supplementaires.
    .filter((i) => i?.byYieldId && i.byYieldId !== 'byperson' && Number(i?.total) > 0);
  if (!items.length) return null;

  const parts = buildParts({ items, t });
  const totalExclTax = Number(data?.riderOwing?.excl_tax);
  const fees = Number(data?.riderFees?.excl_tax) || 0;
  // `priceByDatesBeforeFidelity` couvre les dates seules : on y rajoute les frais
  // pour comparer le meme perimetre que `riderOwing`, sinon le barre serait
  // systematiquement plus bas que le prix affiche.
  const beforeFidelity = Number(data?.priceByDatesBeforeFidelity?.excl_tax) + fees;
  const hasDiscount = Number.isFinite(beforeFidelity)
    && Number.isFinite(totalExclTax)
    && beforeFidelity > totalExclTax;

  const totalHeadline = {
    mode: 'total',
    unit: null,
    unitLabel: null,
    amount: totalExclTax,
    amountMax: null,
    strike: hasDiscount ? round2(beforeFidelity) : null,
    incl_tax: Number(data?.riderOwing?.incl_tax),
    parts,
    count: null,
  };

  const natures = HEADLINE_NATURES.filter((n) => items.some((i) => i.byYieldId === n));
  // Une nature inconnue (ajout futur cote moteur) ne sait pas se nommer : on
  // degrade vers le total plutot que de la passer sous silence.
  const hasUnknownNature = items.some((i) => !HEADLINE_NATURES.includes(i.byYieldId));
  if (natures.length !== 1 || hasUnknownNature) return totalHeadline;

  const nature = natures[0];
  const units = items.map(unitAmountOf);
  if (units.some((u) => u === null || !Number.isFinite(u))) return totalHeadline;

  const min = Math.min(...units);
  const max = Math.max(...units);

  /*
    Le barre porte TOUJOURS sur le meme perimetre que le chiffre affiche. En
    mode `unit` on ne dispose pas du montant unitaire d'avant fidelite : on le
    reconstitue via le ratio des totaux. C'est exact ici parce que toutes les
    dates du groupe partagent le meme montant final — des taux de fidelite
    differents (mois Legend) les auraient separees en plusieurs montants, donc
    en mode `range`. En mode `range`, pas de barre : il faudrait deux
    fourchettes pour rester honnete.
  */
  const ratio = hasDiscount && totalExclTax > 0 ? beforeFidelity / totalExclTax : null;
  // WEA-157 — prix de base (catalogue, sans aucune réduc) par unité, reconstitué via le ratio des
  // totaux : en `unit` un seul barré ; en `range` on expose AUSSI le max (fourchette catalogue),
  // pour barrer la fourchette de base dès qu'il y a une réduction (palier ou abo).
  const strike = ratio ? round2(min * ratio) : null;
  const strikeMax = ratio && min !== max ? round2(max * ratio) : null;

  return {
    mode: min === max ? 'unit' : 'range',
    unit: nature,
    unitLabel: tt({ t, ...UNIT_LABEL[nature] }),
    amount: min,
    amountMax: min === max ? null : max,
    strike,
    strikeMax,
    incl_tax: null,
    parts,
    count: nature === 'hourly'
      ? round2(sumHours({ items }))
      : sumCounters({ items, nature }),
  };
};

/** Les montants du cartridge s'ecrivent sans separateur de milliers (cf. Total). */
export const formatAmount = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace('.', ',');
};
